import { FC } from 'react'
import { RouteComponentProps } from 'react-router'
import { useSelector } from 'react-redux'
import { Helmet } from 'react-helmet'
import { RootStoreState } from 'mytours-read-common'
import { ionHistory } from 'mobile_web/modules/navigation'
import { getProfileUrl, getSettingsUrl } from 'mobile_web/modules/navigation/routes'
import { ImageSizePreset } from 'common_lib/types'
import { isProfileTabShown } from 'mobile_web/modules/profile/functions'
import AudioPlayerBarComponent from 'mobile_web/modules/audio/components/AudioPlayerBar'
import Page from '../../common/components/Page'
import { usePurchasedContent } from '../usePurchasedContent'
import PurchasedContentCard from './PurchasedContentCard'
import strings from '../../../strings'
import './PurchasedContentScreen.less'

// "Browse all" full grid of all purchased/redeemed content. Mirrors CollectionList grid mode
// (.list-block > .list-items.grid with CardImage cards) so it looks identical to a collection.
const PurchasedContentScreen: FC<RouteComponentProps> = () => {
	const { items } = usePurchasedContent()
	const appMetaData = useSelector((state: RootStoreState) => state.settings.appMetaData)

	// The block lives on Profile when that tab is shown, otherwise on Settings; label the back
	// button accordingly and navigate there explicitly (like Favourites) — goBack() would no-op or
	// leave the app when this page is the first history entry (deep link / reload).
	const profileShown = isProfileTabShown(appMetaData)
	const handleBack = (): void => {
		ionHistory.replace(profileShown ? getProfileUrl() : getSettingsUrl())
	}

	return (
		<>
			<Helmet
				title={strings.purchased_content}
				meta={[
					{ name: 'description', content: strings.purchased_content },
					{ property: 'og:type', content: 'website' },
					{ property: 'og:title', content: strings.purchased_content },
					{ property: 'og:description', content: strings.purchased_content },
					{ property: 'og:url', content: window.location.href },
				]}
			/>
			<Page
				contentProps={{ className: 'profile-content' }}
				headerProps={{
					title: strings.purchased_content,
					hideBackButton: true,
					showBackButton: true,
					backButtonLabel: profileShown ? strings.profile : strings.settings,
					onBackButton: handleBack,

					collectionItem: null,
					itemType: null,
					itemId: null,
					collection: null,
					screen: null,
					hideFavouriteButton: true,
				}}
				footer={{ component: <AudioPlayerBarComponent bottomOffset={-1} /> }}
				dataCy="page-purchased-content"
			>
				{items.length === 0 ? (
					// The page is deep-linkable even when the shelf's entry point is hidden, and a
					// revocation can empty it while open — don't leave a blank body.
					<p className="purchased-content-empty">{strings.no_content_available}</p>
				) : (
					<div className="purchased-content-items">
						{items.map(item => (
							<PurchasedContentCard key={item.key} item={item} imageSizePreset={ImageSizePreset.Row} />
						))}
					</div>
				)}
			</Page>
		</>
	)
}

export default PurchasedContentScreen
